<template>
  <div class="filters-bar">
    <div
      v-if="!isLite"
      class="filter-item"
    >
      <select
        v-model="country"
        class="filter-select"
        @change="onCountryFilter"
      >
        <option value="">
          {{ $t('forms.allCountries') }}
        </option>
        <option
          v-for="c in countryOptions"
          :key="c.slug"
          :value="c.slug"
        >
          {{ c.name }}
        </option>
      </select>
    </div>
    <div class="search-group">
      <div class="search-container">
        <input
          v-model="search"
          type="text"
          inputmode="text"
          data-keyboard="text"
          class="search-input"
          :placeholder="$t('forms.searchGamesPlaceholder')"
          @input="onSearch"
        >
        <button
          class="search-button"
          type="button"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed, defineEmits, withDefaults, defineProps } from "vue";
import { useUtilitiesStore } from "@/stores/utilities";
import { useIntegrationMode } from '@/composables/useIntegrationMode'

const props = withDefaults(defineProps<{
  /** When set on mobile, only these country slugs appear in the filter (active draws). */
  activeCountrySlugs?: string[];
}>(), {
  activeCountrySlugs: undefined,
});

const emit = defineEmits<{
  (e: "search", value: string): void;
  (e: "filterCountry", value: string): void;
}>();

const search = ref("");
const country = ref("");
const isMobileViewport = ref(false);

const utilitiesStore = useUtilitiesStore();
const { isLite } = useIntegrationMode()

const updateViewport = () => {
  isMobileViewport.value = typeof window !== 'undefined' && window.innerWidth <= 768;
};

onMounted(() => {
  updateViewport();
  window.addEventListener('resize', updateViewport);
  if (!isLite.value) {
    utilitiesStore.fetchCountries();
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateViewport);
});

const countryOptions = computed(() => {
  const all = utilitiesStore.countries || [];
  const active = props.activeCountrySlugs;
  // Active-draw country filter is mobile-only per Aug 7 review
  if (!isMobileViewport.value || !active || active.length === 0) return all;
  const activeSet = new Set(active.map((slug) => String(slug).toLowerCase()));
  return all.filter((c) => activeSet.has(String(c.slug).toLowerCase()));
});

watch(countryOptions, (options) => {
  if (country.value && !options.some((c) => c.slug === country.value)) {
    country.value = '';
    emit('filterCountry', '');
  }
});

const onSearch = () => emit("search", search.value);
const onCountryFilter = () => emit("filterCountry", country.value);

watch([search, country], () => {
  emit("search", search.value);
  emit("filterCountry", country.value);
});
</script>

<script lang="ts">
export default {
  name: "LotteryFilters",
};
</script>

<style scoped>
.filters-bar {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 18px;
  margin-top: 0;
  /* Match header→content gap after filters as well */
  margin-bottom: var(--section-header-simple-margin-bottom, 16px);
  flex-wrap: wrap;
  flex: 1;
  width: 100%;
}

/* When used in date-filter-section, remove bottom margin */
.date-filter-section .filters-bar {
  margin-bottom: 0;
  flex: 1;
  min-width: 0;
}

.filter-item {
  flex: 1;
  min-width: 0;
  max-width: 300px;
}

.search-group {
  flex: 1;
  min-width: 0;
  max-width: 300px;
}

.search-container {
  display: flex;
  align-items: center;
  /* background: var(--colour-surface-cont-lowest); */
  border-radius: var(--results-filter-radius, 8px);
  border: 1px solid var(--colour-surface-cont);
  width: 100%;
  height: 54px;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
  box-shadow: var(--shadow-control);
  transition: none;
}

.search-container:focus-within {
  border-color: var(--colour-surface-cont);
  box-shadow: none;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  color: var(--colour-base-text-colour);
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 16px;
  font-weight: 500;
  outline: none;
  padding: 0 18px;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}

.search-input::placeholder {
  color: var(--text-control-placeholder, var(--colour-base-text-colour));
  opacity: 1;
}

.search-button {
  background: var(--colour-surface-cont-low) !important;
  border: 0;
  border-left: 1px solid color-mix(in srgb, var(--colour-surface-cont) 88%, transparent);
  color: var(--text-control-strong, var(--colour-text-inverse)) !important;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 100%;
  flex-shrink: 0;
  transition: none;
}

.search-button svg {
  stroke: currentColor;
}


/* Large screens (1200px+) - Enhanced layout */
@media (min-width: 1200px) {
  .filters-bar {
    gap: 20px;
    margin-bottom: var(--section-header-simple-margin-bottom, 16px);
    justify-content: center;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .filter-item {
    max-width: 350px;
  }
  
  .search-group {
    max-width: 350px;
  }
  
.search-container {
    height: 50px;
    padding: 0;
  }
  
  .search-input {
    font-size: 16px;
    padding: 0 16px;
  }
  
  .search-button {
    width: 50px;
    height: 100%;
    padding: 0;
  }
}

/* Medium screens (1024px - 1199px) - Balanced layout */
@media (min-width: 1024px) and (max-width: 1199px) {
  .filters-bar {
    gap: 18px;
    margin-bottom: var(--section-header-simple-margin-bottom, 16px);
    justify-content: center;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .filter-item {
    max-width: 320px;
  }
  
  .search-group {
    max-width: 320px;
  }
  
  .search-container {
    height: 50px;
    padding: 0;
  }
  
  .search-input {
    font-size: 16px;
    padding: 0 16px;
  }
  
  .search-button {
    width: 50px;
    height: 100%;
    padding: 0;
  }
}

/* Tablet/iPad (768px - 1023px) - Optimized for tablet */
@media (min-width: 768px) and (max-width: 1023px) {
  .filters-bar {
    gap: 16px;
    margin-bottom: var(--section-header-simple-margin-bottom, 16px);
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    flex-wrap: nowrap;
  }
  
  .filter-item {
    flex: 1;
    max-width: none;
    min-width: 0;
  }
  
  .search-group {
    flex: 1;
    max-width: none;
    min-width: 0;
  }
  
  .search-container {
    height: 50px;
    padding: 0;
  }
  
  .search-input {
    font-size: 15px;
    padding: 0 16px;
  }
  
  .search-button {
    width: 46px;
    height: 100%;
    padding: 0;
  }
}

/* Mobile (≤767px) - Clean side-by-side layout */
@media (max-width: 767px) {
  .filters-bar {
    flex-direction: row;
    gap: 16px;
    margin-top: 0;
    margin-bottom: var(--section-header-simple-mobile-margin-bottom, 16px);
    align-items: stretch;
    flex-wrap: nowrap;
  }
  
  .filter-item {
    flex: 1;
    max-width: none;
    min-width: 0;
  }
  
  .search-group {
    flex: 1;
    max-width: none;
    min-width: 0;
  }
  
  .search-container {
    height: 50px;
    padding: 0;
    border-radius: var(--results-filter-radius, 8px);
    border: 1px solid var(--border-dark, var(--colour-surface-cont));
    background: transparent;
  }
  
  .search-input {
    font-size: 16px;
    padding: 0 16px;
  }
  
  .search-button {
    width: 50px;
    height: 100%;
    padding: 0;
  }
  
  .search-button svg {
    width: 20px;
    height: 20px;
  }
  
  .filter-select {
    height: 50px !important;
    font-size: 16px !important;
    padding: 0 48px 0 16px !important;
    border-radius: var(--results-filter-radius, 8px) !important;
    border: 1px solid var(--border-dark, var(--colour-surface-cont)) !important;
  }
}

/* Small mobile (≤480px) - Maintain clean layout */
@media (max-width: 480px) {
  .filters-bar {
    gap: 12px;
    margin-top: 0;
    margin-bottom: var(--section-header-simple-mobile-margin-bottom, 16px);
  }
  
  .search-container {
    height: 48px;
  }
  
  .search-input {
    /* Keep ≥16px on mobile to avoid iOS auto-zoom */
    font-size: 16px;
    padding: 0 14px;
  }
  
  .search-button {
    width: 48px;
  }
  
  .search-button svg {
    width: 18px;
    height: 18px;
  }
  
  .filter-select {
    height: 48px !important;
    font-size: 16px !important;
    padding: 0 44px 0 14px !important;
  }
}

.filter-select {
  width: 100%;
  background: transparent;
  border-radius: var(--results-filter-radius, 8px);
  border: 1px solid var(--colour-surface-cont);
  color: var(--colour-base-text-colour);
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: 16px;
  padding: 0 52px 0 18px;
  font-weight: var(--filter-select-font-weight, 600);
  cursor: pointer;
  outline: none;
  box-sizing: border-box;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  height: 54px;
  background-image: var(--bg-filter-select-img, url("data:image/svg+xml,%3Csvg width='14' height='8' viewBox='0 0 14 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L7 7L13 1' stroke='%23101520' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"));
  background-repeat: no-repeat;
  background-position: right 18px center;
  box-shadow: none;
  transition: none;
}

.filter-select:focus {
  border-color: var(--filter-select-focus-border-color, var(--colour-surface-cont));
  box-shadow: none;
}

.filter-select option {
  background: var(--colour-surface-cont-lowest);
  color: var(--colour-base-text-colour);
  padding: 12px 16px;
}
</style>
